import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Send, Loader2, Lock, MessageCircle } from 'lucide-react';
import WhatsAppSendDialog from '@/components/whatsapp/WhatsAppSendDialog';

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};
const preview = (c) => c.last_text || (c.last_template ? `Template · ${c.last_template}` : '—');

export const WhatsAppInbox = () => {
  const [convos, setConvos] = useState([]);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/whatsapp/conversations', { params: { q: q || undefined, limit: 150 } }); setConvos(data.data || []); }
    catch { toast.error('Failed to load inbox'); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] border border-stone-200 rounded-lg bg-white overflow-hidden h-[620px]" data-testid="wa-inbox">
      <div className="border-r border-stone-200 flex flex-col min-h-0">
        <div className="p-2 border-b border-stone-100 relative">
          <Search className="w-3.5 h-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or number…" className="h-8 pl-7 text-xs" data-testid="wa-inbox-search" />
        </div>
        <div className="flex-1 overflow-auto hc-scrollbar">
          {loading && convos.length === 0 && <div className="p-4 text-xs text-stone-400">Loading…</div>}
          {!loading && convos.length === 0 && <div className="p-4 text-xs text-stone-400">No conversations.</div>}
          {convos.map((c) => (
            <button key={c.msisdn} type="button" onClick={() => setActive(c)} data-testid={`wa-convo-${c.msisdn}`}
              className={`w-full text-left px-3 py-2.5 border-b border-stone-100 flex gap-2 transition-colors ${active?.msisdn === c.msisdn ? 'bg-emerald-50' : 'hover:bg-stone-50'}`}>
              <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center text-[11px] font-bold shrink-0">
                {(c.patient_name || '#').slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1">
                  <span className="text-xs font-semibold text-stone-900 truncate">{c.patient_name || `+${c.msisdn}`}</span>
                  <span className="ml-auto text-[10px] text-stone-400 tabular-nums shrink-0">{when(c.last_at)}</span>
                </div>
                <div className="flex items-center gap-1 mt-0.5">
                  <span className={`text-[11px] truncate ${c.last_direction === 'in' ? 'text-stone-900 font-medium' : 'text-stone-500'}`}>{preview(c)}</span>
                  {c.session_open && <span className="ml-auto w-2 h-2 rounded-full bg-emerald-500 shrink-0" title="24h window open" />}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
      {active ? <Thread convo={active} onSent={load} /> : (
        <div className="hidden md:flex flex-col items-center justify-center text-stone-400 gap-2">
          <MessageCircle className="w-8 h-8" /><span className="text-xs">Select a conversation</span>
        </div>
      )}
    </div>
  );
};

function Thread({ convo, onSent }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/whatsapp/messages', { params: { patient_mobile: convo.msisdn, limit: 200 } });
      setMsgs((data.data || []).slice().reverse());
    } catch { toast.error('Failed to load thread'); }
  }, [convo.msisdn]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post('/whatsapp/send-session-text', { to: convo.msisdn, text: text.trim(), ticket_id: convo.ticket_id });
      setText(''); load(); onSent && onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    finally { setSending(false); }
  };

  return (
    <div className="flex flex-col min-h-0" data-testid="wa-thread">
      <div className="px-4 py-2.5 border-b border-stone-200 flex items-center gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-stone-900 truncate">{convo.patient_name || 'Unknown'}</div>
          <div className="text-[11px] text-stone-500 font-mono">+{convo.msisdn}</div>
        </div>
        <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full font-semibold ${convo.session_open ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-600'}`}>
          {convo.session_open ? '24h window open' : 'Window closed'}
        </span>
      </div>
      <div className="flex-1 overflow-auto hc-scrollbar p-4 space-y-2 bg-[#efeae2]">
        {msgs.map((m) => {
          const inbound = m.direction === 'in';
          return (
            <div key={m.id} className={`flex ${inbound ? 'justify-start' : 'justify-end'}`} data-testid={`wa-bubble-${m.id}`}>
              <div className={`max-w-[75%] rounded-lg px-3 py-1.5 text-xs shadow-sm ${inbound ? 'bg-white text-stone-900' : 'bg-[#d9fdd3] text-stone-900'}`}>
                {m.text ? <div className="whitespace-pre-wrap">{m.text}</div> : <div className="italic text-stone-600">Template: {m.template_id}</div>}
                <div className="text-[9px] text-stone-500 mt-0.5 text-right">
                  {new Date(m.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  {!inbound && ` · ${m.delivery_status || m.status || ''}`}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="p-2 border-t border-stone-200 flex items-center gap-2">
        {convo.session_open ? (
          <>
            <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
              placeholder="Type a reply…" className="h-9 text-sm" data-testid="wa-reply-input" />
            <Button onClick={send} disabled={sending || !text.trim()} className="h-9 bg-emerald-600 hover:bg-emerald-700" data-testid="wa-reply-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </>
        ) : (
          <>
            <Lock className="w-4 h-4 text-stone-400" />
            <span className="text-xs text-stone-500 flex-1">Free replies open for 24h after the patient messages. Send an approved template instead.</span>
            <Button size="sm" onClick={() => setTplOpen(true)} className="h-8 bg-emerald-600 hover:bg-emerald-700" data-testid="wa-send-template">Send template</Button>
          </>
        )}
      </div>
      <WhatsAppSendDialog open={tplOpen} onOpenChange={setTplOpen} to={convo.msisdn} patientName={convo.patient_name || ''}
        ticketId={convo.ticket_id} onSent={() => { load(); onSent && onSent(); }} />
    </div>
  );
}
